import { lazy, Suspense } from 'react'
import {
    createRootRouteWithContext, createRoute, createRouter, lazyRouteComponent, Outlet, redirect, useRouter,
    type ErrorComponentProps,
} from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { SponsorApiError } from '@/lib/api'
import { ADOPT_GENDERS, CATALOG_AGE_BANDS } from '@/lib/contract'
import { bootstrapQuery, meQuery } from '@/lib/queries'
import { isOnboardingDone } from '@/stores/onboarding.store'
import { rememberReturnPath } from '@/lib/navigation'
import { BrandProvider } from '@/components/shell/BrandProvider'
import { AppShell } from '@/components/shell/AppShell'
import { AuthLayout } from '@/components/shell/AuthLayout'
import { SplashScreen } from '@/features/system/components/SplashScreen'
import { NetworkErrorScreen, NotFoundScreen, UnavailableScreen } from '@/features/system/components/StatusScreens'

/* Cada tela e carregada sob demanda (code-splitting): o primeiro acesso baixa so o necessario (SC-006). */
const OnboardingPage = lazyRouteComponent(() => import('@/features/onboarding/page/OnboardingPage'), 'OnboardingPage')
const LoginPage = lazyRouteComponent(() => import('@/features/login/page/LoginPage'), 'LoginPage')
const SignupPage = lazyRouteComponent(() => import('@/features/signup/page/SignupPage'), 'SignupPage')
const PasswordRecoveryPage = lazyRouteComponent(() => import('@/features/passwordRecovery/page/PasswordRecoveryPage'), 'PasswordRecoveryPage')
const CodePage = lazyRouteComponent(() => import('@/features/login/page/CodePage'), 'CodePage')
const LinkLandingPage = lazyRouteComponent(() => import('@/features/login/page/LinkLandingPage'), 'LinkLandingPage')
const HomePage = lazyRouteComponent(() => import('@/features/home/page/HomePage'), 'HomePage')
const NoticesPage = lazyRouteComponent(() => import('@/features/notices/page/NoticesPage'), 'NoticesPage')
const MessagesPage = lazyRouteComponent(() => import('@/features/messages/page/MessagesPage'), 'MessagesPage')
const ThreadPage = lazyRouteComponent(() => import('@/features/messages/page/ThreadPage'), 'ThreadPage')
const ChildrenListPage = lazyRouteComponent(() => import('@/features/childrenProfiles/page/ChildrenListPage'), 'ChildrenListPage')
const ChildProfilePage = lazyRouteComponent(() => import('@/features/childrenProfiles/page/ChildProfilePage'), 'ChildProfilePage')
const ChildTimelinePage = lazyRouteComponent(() => import('@/features/childrenProfiles/page/ChildTimelinePage'), 'ChildTimelinePage')
const AccountPage = lazyRouteComponent(() => import('@/features/account/page/AccountPage'), 'AccountPage')
const HelpPage = lazyRouteComponent(() => import('@/features/help/page/HelpPage'), 'HelpPage')
const SettingsPage = lazyRouteComponent(() => import('@/features/settings/page/SettingsPage'), 'SettingsPage')
const AccessibilityPage = lazyRouteComponent(() => import('@/features/accessibility/page/AccessibilityPage'), 'AccessibilityPage')
const GivingPage = lazyRouteComponent(() => import('@/features/giving/page/GivingPage'), 'GivingPage')
const CheckoutPage = lazyRouteComponent(() => import('@/features/giving/page/CheckoutPage'), 'CheckoutPage')
const DonationsPage = lazyRouteComponent(() => import('@/features/donations/page/DonationsPage'), 'DonationsPage')
const AppSponsorshipPage = lazyRouteComponent(() => import('@/features/donations/page/AppSponsorshipPage'), 'AppSponsorshipPage')
const VisitPage = lazyRouteComponent(() => import('@/features/visit/page/VisitPage'), 'VisitPage')
const ImpactPage = lazyRouteComponent(() => import('@/features/impact/page/ImpactPage'), 'ImpactPage')
// Spec PWA-015: apadrinhar uma nova crianca
const CatalogPage = lazyRouteComponent(() => import('@/features/adopt/page/CatalogPage'), 'CatalogPage')
const CatalogChildPage = lazyRouteComponent(() => import('@/features/adopt/page/CatalogChildPage'), 'CatalogChildPage')
const AdoptCheckoutPage = lazyRouteComponent(() => import('@/features/adopt/page/AdoptCheckoutPage'), 'AdoptCheckoutPage')
const AdoptWelcomePage = lazyRouteComponent(() => import('@/features/adopt/page/AdoptWelcomePage'), 'AdoptWelcomePage')

/* Spec PWA-002 — seletor de impersonacao (dados reais do CRM). Condicao inline: sem a flag, o bundler elimina o modulo. */
const ImpersonationRoot = import.meta.env.VITE_SPONSOR_IMPERSONATION === 'true'
    ? lazy(() => import('@/dev/impersonation/ImpersonationRoot').then(m => ({ default: m.ImpersonationRoot })))
    : null

/* Spec PWA-001 — arvore de rotas do App do Padrinho.
   Publicas: /boas-vindas (onboarding, so no 1o uso), /entrar, /cadastro, /recuperar-senha, /codigo, /l/:token.
   Autenticadas com barra inferior (_tabs): /inicio, /mensagens, /doar, /impacto, /conta, /criancas.
   Autenticadas em tela cheia: /comunicados, /mensagens/:id, /criancas/:ref, /doar/pagamento, /doacoes, /visita,
   /ajuda, /configuracoes, /acessibilidade. */

type RouterContext = { queryClient: QueryClient }

async function isAuthenticated(queryClient: QueryClient): Promise<boolean> {
    try {
        await queryClient.ensureQueryData(meQuery)
        return true
    } catch (error) {
        if (error instanceof SponsorApiError && error.code === 'SPONSOR_SESSION_REQUIRED') return false
        throw error
    }
}

function RootError({ error }: ErrorComponentProps) {
    const router = useRouter()
    if (error instanceof SponsorApiError && error.code === 'SPONSOR_APP_UNAVAILABLE') return <UnavailableScreen />
    return <NetworkErrorScreen onRetry={() => void router.invalidate()} />
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
    beforeLoad: async ({ context }) => ({
        bootstrap: await context.queryClient.ensureQueryData(bootstrapQuery),
    }),
    component: function Root() {
        const { bootstrap } = rootRoute.useRouteContext()
        return (
            <BrandProvider bootstrap={bootstrap}>
                {ImpersonationRoot ? <Suspense fallback={null}><ImpersonationRoot /></Suspense> : null}
                <Outlet />
            </BrandProvider>
        )
    },
    errorComponent: RootError,
    pendingComponent: SplashScreen,
    notFoundComponent: () => <main className="min-h-dvh bg-app-background"><NotFoundScreen /></main>,
})

const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    beforeLoad: async ({ context }) => {
        if (await isAuthenticated(context.queryClient)) throw redirect({ to: '/inicio', replace: true })
        throw redirect({ to: isOnboardingDone() ? '/entrar' : '/boas-vindas', replace: true })
    },
})

/* Telas publicas: quem ja esta autenticado vai direto para o Inicio. */
const redirectIfAuthenticated = async ({ context }: { context: RouterContext }) => {
    if (await isAuthenticated(context.queryClient)) throw redirect({ to: '/inicio', replace: true })
}

const onboardingRoute = createRoute({ getParentRoute: () => rootRoute, path: '/boas-vindas', beforeLoad: redirectIfAuthenticated, component: OnboardingPage })

const enterRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/entrar',
    validateSearch: z.object({
        motivo: z.enum(['link-invalido', 'senha-redefinida']).optional().catch(undefined),
    }),
    beforeLoad: redirectIfAuthenticated,
    component: LoginPage,
})

const signupRoute = createRoute({ getParentRoute: () => rootRoute, path: '/cadastro', beforeLoad: redirectIfAuthenticated, component: SignupPage })
const recoveryRoute = createRoute({ getParentRoute: () => rootRoute, path: '/recuperar-senha', beforeLoad: redirectIfAuthenticated, component: PasswordRecoveryPage })

const codeRoute = createRoute({ getParentRoute: () => rootRoute, path: '/codigo', component: CodePage })
const linkRoute = createRoute({ getParentRoute: () => rootRoute, path: '/l/$token', component: LinkLandingPage })

/* Guarda de sessao para toda a area autenticada. PWA-014 D-12: guarda o caminho pedido (ex.: aviso tocado sem
   sessao) para abrir depois de entrar. */
const authRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: '_auth',
    beforeLoad: async ({ context, location }) => {
        if (!(await isAuthenticated(context.queryClient))) {
            rememberReturnPath(location.href)
            throw redirect({ to: '/entrar', replace: true })
        }
    },
    component: AuthLayout,
})

const tabsRoute = createRoute({ getParentRoute: () => authRoute, id: '_tabs', component: AppShell })

const homeRoute = createRoute({
    getParentRoute: () => tabsRoute,
    path: '/inicio',
    // PWA-029: o aviso de evento na cidade abre o pop-up de eventos nele
    validateSearch: z.object({ evento: z.string().uuid().optional().catch(undefined) }),
    component: HomePage,
})
const messagesRoute = createRoute({ getParentRoute: () => tabsRoute, path: '/mensagens', component: MessagesPage })
const childrenRoute = createRoute({ getParentRoute: () => tabsRoute, path: '/criancas', component: ChildrenListPage })
const accountRoute = createRoute({ getParentRoute: () => tabsRoute, path: '/conta', component: AccountPage })
const givingRoute = createRoute({ getParentRoute: () => tabsRoute, path: '/doar', component: GivingPage })
const impactRoute = createRoute({ getParentRoute: () => tabsRoute, path: '/impacto', component: ImpactPage })

const noticesRoute = createRoute({ getParentRoute: () => authRoute, path: '/comunicados', component: NoticesPage })
const threadRoute = createRoute({ getParentRoute: () => authRoute, path: '/mensagens/$threadId', component: ThreadPage })
const childProfileRoute = createRoute({ getParentRoute: () => authRoute, path: '/criancas/$childRef', component: ChildProfilePage })
// Spec PWA-027: linha do tempo do apadrinhamento
const childTimelineRoute = createRoute({ getParentRoute: () => authRoute, path: '/criancas/$childRef/historia', component: ChildTimelinePage })
const helpRoute = createRoute({ getParentRoute: () => authRoute, path: '/ajuda', component: HelpPage })
const settingsRoute = createRoute({ getParentRoute: () => authRoute, path: '/configuracoes', component: SettingsPage })
const accessibilityRoute = createRoute({ getParentRoute: () => authRoute, path: '/acessibilidade', component: AccessibilityPage })
const checkoutRoute = createRoute({
    getParentRoute: () => authRoute,
    path: '/doar/pagamento',
    validateSearch: z.object({
        produto: z.string().min(1).max(64).optional().catch(undefined),
        valor: z.coerce.number().int().positive().optional().catch(undefined),
    }),
    component: CheckoutPage,
})
const donationsRoute = createRoute({ getParentRoute: () => authRoute, path: '/doacoes', component: DonationsPage })
// Spec PWA-019: apadrinhamento feito pelo app (pelo protocolo)
const appSponsorshipRoute = createRoute({ getParentRoute: () => authRoute, path: '/doacoes/apadrinhamentos/$protocol', component: AppSponsorshipPage })
// Spec PWA-015: catalogo (?genero=&idade=&uf=), ficha, pagamento e boas-vindas
const adoptRoute = createRoute({
    getParentRoute: () => authRoute,
    path: '/apadrinhar',
    validateSearch: z.object({
        genero: z.enum(ADOPT_GENDERS).optional().catch(undefined),
        idade: z.enum(CATALOG_AGE_BANDS).optional().catch(undefined),
        uf: z.string().regex(/^[A-Z]{2}$/).optional().catch(undefined),
    }),
    component: CatalogPage,
})
const adoptChildRoute = createRoute({ getParentRoute: () => authRoute, path: '/apadrinhar/$childId', component: CatalogChildPage })
const adoptCheckoutRoute = createRoute({ getParentRoute: () => authRoute, path: '/apadrinhar/$childId/pagamento', component: AdoptCheckoutPage })
const adoptWelcomeRoute = createRoute({ getParentRoute: () => authRoute, path: '/apadrinhar/$childId/bem-vindo', component: AdoptWelcomePage })
const visitRoute = createRoute({
    getParentRoute: () => authRoute,
    path: '/visita',
    validateSearch: z.object({ crianca: z.string().min(1).max(200).optional().catch(undefined) }),
    component: VisitPage,
})

const routeTree = rootRoute.addChildren([
    indexRoute,
    onboardingRoute,
    enterRoute,
    signupRoute,
    recoveryRoute,
    codeRoute,
    linkRoute,
    authRoute.addChildren([
        tabsRoute.addChildren([homeRoute, messagesRoute, givingRoute, impactRoute, accountRoute, childrenRoute]),
        noticesRoute,
        threadRoute,
        childProfileRoute,
        childTimelineRoute,
        helpRoute,
        settingsRoute,
        accessibilityRoute,
        checkoutRoute,
        donationsRoute,
        appSponsorshipRoute,
        visitRoute,
        adoptRoute,
        adoptChildRoute,
        adoptCheckoutRoute,
        adoptWelcomeRoute,
    ]),
])

export function createAppRouter(queryClient: QueryClient) {
    return createRouter({
        routeTree,
        context: { queryClient },
        defaultPreload: 'intent',
        defaultPendingComponent: SplashScreen,
        scrollRestoration: true,
    })
}

declare module '@tanstack/react-router' {
    interface Register {
        router: ReturnType<typeof createAppRouter>
    }
}
